import React, { useEffect, useState} from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, AppBar, Typography, Button, Box, Toolbar} from '@mui/material';
import Login from './components/Login/Login.tsx'; // Adjust the import path as necessary
import SummaryDashboard from './Scenes/SummaryDashboard/SummaryDashboard.tsx';
import ControlPanel from './Scenes/ControlPanel/ControlPanel.tsx';
import PropertiesList from './Scenes/PropertiesList/PropertiesList.tsx';
import { fetchProperties, fetchRawProperties } from './helpers.js';
import { verifyRoute } from './constants.ts';

// --- Date helpers (ET-aware "today") ---
const toETDateString = (d) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);

// Try a few common date fields that might exist on a property
const parsePropDate = (p) => {
  const raw =
    p?.found_at ||
    p?.created_at ||
    p?.createdAt ||
    p?.date ||
    p?.timestamp ||
    null;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const isFoundTodayET = (p) => {
  const d = parsePropDate(p);
  if (!d) return false;
  return toETDateString(d) === toETDateString(new Date());
};
// Parse dates for RAW properties (common fields)
const parseRawDate = (p) => {
  const raw =
    p?.scrapedAt ||      // preferred for raw docs
    p?.scraped_at ||     // snake_case variant
    p?.created_at ||
    p?.createdAt ||
    p?.timestamp ||
    null;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const isRawFoundTodayET = (p) => {
  const d = parseRawDate(p);
  if (!d) return false;
  return toETDateString(d) === toETDateString(new Date());
};
// Define a dark theme to match the previous style
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#111827', 
      paper: '#1F2937',   
  },
},
  typography: {
    fontFamily: 'Roboto, sans-serif',
  },
});

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [verifiedUser, setVerifiedUser] = useState(null);
  const [isControlsHidden, setIsControlsHidden] = useState(true);
  const [isPropertiesListHidden, setIsPropertiesListHidden] = useState(true); 
  const [properties, setProperties] = useState([]);
  const [rawProperties, setRawProperties] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      fetch(verifyRoute, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(res => res.text())
        .then(text => {
          try {
            const data = JSON.parse(text);
            if (data.success) {
              setVerifiedUser(data.user);
              setIsLoggedIn(true)
            } else {
              localStorage.removeItem('authToken');
            }
          } catch (err) {
            console.error('Failed to parse JSON from /api/auth/verify:', err);
            localStorage.removeItem('authToken');
          }
        })
    }
  }, []);

  const checkVerification = (data) => {
    if (data) {
      setIsLoggedIn(true);
      setVerifiedUser(data);
    } else {
      setIsLoggedIn(false);
      setVerifiedUser(null);
    }
  }

  const emailsSent = properties.filter(property => property.email_sent === true).length
  // Totals based on ET
  const foundToday = properties.filter(isFoundTodayET).length;

  const last30Cutoff = (() => {
    // 30 calendar days back from "now"
    const now = new Date();
    const cutoff = new Date(now);
    cutoff.setDate(now.getDate() - 30);
    return cutoff;
  })();

  const foundLast30Days = properties.filter(p => {
    const d = parsePropDate(p);
    return d && d >= last30Cutoff;
  }).length;

  const rawFoundToday = rawProperties.filter(isRawFoundTodayET).length;

  const rawFoundLast30Days = rawProperties.filter(p => {
    const d = parseRawDate(p);
    return d && d >= last30Cutoff;
  }).length;
  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    fetchRawProperties(setRawProperties);
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    fetchProperties(setProperties);
  }, [isLoggedIn]);

  const toggleControls = () => {
    setIsControlsHidden(!isControlsHidden);
    setIsPropertiesListHidden(true);
  };

  const togglePropertiesList = () => {
    setIsPropertiesListHidden(!isPropertiesListHidden);
    setIsControlsHidden(true);
  };

  const toggleSummary = () => {
    setIsControlsHidden(true);
    setIsPropertiesListHidden(true);
  };




  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar position="fixed">
        <Toolbar>
            {isLoggedIn && (
              <Button color="primary" variant="contained" onClick={toggleControls}>
                Control Panel
              </Button>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexGrow: 1 }}>
              {/* Logo */}
              <img 
                src="/mioym_logo.png" 
                alt="Mioym Logo" 
                style={{ height: '50px', marginBottom: '8px' }} 
              />
              {/* Title */}
              <Typography 
                variant="h5" 
                component="div" 
                sx={{ fontWeight: 'bold', textAlign: 'center', color: 'white' }} 
                onClick={toggleSummary}
              >
              Deal Finder
              </Typography>
            </Box>
            {isLoggedIn && (
              <div>
                <Button color="primary" variant="contained" onClick={togglePropertiesList}>
                  Deals
                </Button>
                <Button color="secondary" variant="outlined" sx={{ ml: 1 }} onClick={() => {
                  localStorage.removeItem('authToken');
                  setIsLoggedIn(false);
                  setVerifiedUser(null);
                }}>
                  Logout
                </Button>
              </div>
            )}
          </Toolbar>
        </AppBar>
        <Box  mt='75px' alignItems='center' justifyContent='center'>
          {/* The Login component is now rendered as the main content */}
          { !isLoggedIn && (
            <Login verify={checkVerification} />
          )}
          {
            /* If the user is logged in, render the main content */
            isLoggedIn && isControlsHidden && isPropertiesListHidden && (
              <Box sx={{ flexGrow: 1, p: 3, alignItems: 'center', justifyContent: 'center' }}>
                <SummaryDashboard 
                  totalRawPropsFoundToday={rawFoundToday}
                  totalRawPropsFoundLast30days={rawFoundLast30Days} 
                  totalFoundToday={foundToday} 
                  totalFoundLast30days={foundLast30Days} 
                  emailsSent={emailsSent}/>
              </Box>
            )
          }
          { !isControlsHidden && verifiedUser.isAdmin && (
            <Box sx={{ p: 3, textAlign: 'center', backgroundColor: '#1F2937', color: 'white' }}>
              <ControlPanel />
            </Box>
          )}

          { isControlsHidden && isLoggedIn && !isPropertiesListHidden && (
            <Box sx={{ p: 3, textAlign: 'center', backgroundColor: '#1F2937', color: 'white' }}>
              <PropertiesList properties={properties}/>
            </Box>
          )}
        </Box>
          <Box 
            component="footer" 
            sx={{ 
              mt: 'auto', 
              py: 2, 
              textAlign: 'center', 
              backgroundColor: '#1F2937', 
              color: 'white', 
              fontSize: '0.875rem',
              position: 'fixed',
              bottom: 0,
              left: 0,
              width: '100%',
              zIndex: 1300
            }}
          >
            © 2025 Mioym. All Rights Reserved | Designed &amp; Developed By{' '}
            <a 
              href="https://www.hmtechz.com/" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Hakuna Matata Technologies
            </a>
          </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
